import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { RouterService } from '@core/services/router.service';
import { StorageService } from '@core/services/storage.service';
import type { ZoneSelectionMode } from '@core/types/zone';
import { PAGE_BASE, isProd } from '@core/utils/environment';
import { IS_HETAO_OJ } from '@core/utils/runtime-config';

@Injectable()
export class ZoneService {
  public zone = '';
  // 只有在第一次访问页面时，本地没有zone时才会弹出选择域的弹窗
  // 随后再刷新页面，因为本地已经有zone了，所以不会再弹出选择域的弹窗
  public showSelectZoneModal = false;
  private hasConfirmedZonePreference = false;

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  public isCpp() {
    return this.zone === 'cpp';
  }

  public isPython() {
    return this.zone === 'python';
  }

  public getSiteUrl() {
    return IS_HETAO_OJ ? `${PAGE_BASE}/${this.zone}` : PAGE_BASE;
  }

  public getZoneBase() {
    return IS_HETAO_OJ ? `/${this.zone}` : '';
  }

  /**
   * 初始化zone
   */
  public initZone(zone: string) {
    this.zone = zone;
    if (IS_HETAO_OJ) {
      // 只有hetao OJ需要处理域
      // saas OJ不需要处理域
      this.updateZoneIfLocalEmpty(zone);
    }
  }

  /**
   * 更新本地的zone
   */
  public updateZone(zone: string) {
    this.zone = zone;
    this.storageService.setZone(zone);
    this.routerService.updateZone(zone);
  }

  /**
   * 客户端登录默认记住入口语言，但不能覆盖本次访问中用户已明确确认的偏好。
   */
  public rememberCurrentZoneAsDefault() {
    if (!this.hasConfirmedZonePreference) {
      this.storageService.setZone(this.zone);
    }
  }

  /**
   * 如果本地zone为空，更新本地zone
   */
  public updateZoneIfLocalEmpty(zone: string) {
    const localZone = this.storageService.getZone();
    if (!localZone) {
      this.zone = zone;
      this.showSelectZoneModal = true;
      this.storageService.setZone(zone);
      this.storageService.setZoneGuideNeedShow();
    }
  }

  /**
   * 通过弹窗来选择默认的zone
   * 选择完毕后更新本地zone并且设置zone引导
   */
  public updateZoneFromModal(
    selectedZone: string,
    mode: ZoneSelectionMode = 'navigate',
  ) {
    this.storageService.setZoneGuideNeedShow();
    if (mode === 'preference-only') {
      // 流程页只保存偏好，当前运行语言和在途请求继续使用原 URL 所属域。
      this.storageService.setZone(selectedZone);
    } else {
      this.updateZone(selectedZone);
    }
    this.hasConfirmedZonePreference = true;
    this.showSelectZoneModal = false;
  }

  public removeZoneGuide() {
    this.storageService.removeZoneGuide();
  }

  public needShowZoneGuide() {
    return this.storageService.isZoneGuideNeedShow();
  }

  public needShowZoneModal() {
    return this.showSelectZoneModal;
  }
}
